import { toast } from "sonner";
import { useState } from "react";
import { api } from "@/lib/api";
import Counter from "@/components/Counter";
import { Seo } from "@/components/Seo";

const TRACKS = [
  { n: "01", title: "Identity & Belonging", body: "Foundational work on selfhood, family story, and the dignity of being seen." },
  { n: "02", title: "Education & Mastery", body: "Academic confidence, study craft, and lifelong intellectual ownership." },
  { n: "03", title: "Leadership & Voice", body: "Practical leadership grounded in self-knowledge and public service." },
  { n: "04", title: "Ownership & Enterprise", body: "Financial literacy, asset-building, and entrepreneurial pathways." },
  { n: "05", title: "Mentorship & Network", body: "Building intergenerational mentorship as a structural advantage." },
  { n: "06", title: "Health & Resilience", body: "Mental, physical, and spiritual practices for long-form endurance." },
  { n: "07", title: "Legacy & Contribution", body: "Translating personal mastery into community transformation." },
];

const TESTIMONIALS = [
  { name: "Dr. Lena Marsh", role: "Dean of Student Programs", body: "TCPI is the most coherent integrative program I've reviewed in two decades. Our cohorts come out measurably more grounded." },
  { name: "Antoine Rivers", role: "Pilot Cohort, 2024", body: "It was the first time anyone explained ownership to me as something I could practice, not just something I could envy." },
  { name: "City of Greendale", role: "Institutional Partner", body: "We brought TCPI to our after-school programs. The reading and savings outcomes spoke for themselves." },
];

const IMPACT = [
  { v: 4800, s: "+", l: "Students Reached" },
  { v: 36, s: "", l: "Communities" },
  { v: 14, s: "", l: "Institutional Partners" },
  { v: 92, s: "%", l: "Completion Rate" },
];

export default function TCPI() {
  return (
    <div data-testid="tcpi-page">
      <Seo
        title="The Crown Path Initiative (TCPI) | 1123 Publishing Company"
        description="The Crown Path Initiative is a seven-track transformation curriculum spanning identity, education, leadership, ownership, mentorship, resilience, and legacy — adopted by schools and community partners."
        path="/tcpi"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: "The Crown Path Initiative",
          description:
            "A seven-track transformation curriculum across identity, education, leadership, ownership, mentorship, resilience, and legacy.",
          provider: { "@type": "Organization", name: "1123 Publishing Company", url: "https://1123publishing.com" },
        }}
      />
      <TcpiHero />
      <TracksGrid />
      <ImpactStats />
      <TestimonialGrid />
      <InquiryForm />
    </div>
  );
}

function TcpiHero() {
  return (
    <section className="bg-navy text-ivory py-24 md:py-32 relative overflow-hidden">
      <div className="parchment-overlay" />
      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="overline text-gold mb-4">The Crown Path Initiative</div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-14 mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_publish-legacy/artifacts/5ac160dh_file_00000000a58871f5bb9b7b728fcb5be0.png"
            alt="The Crown Path Initiative"
            className="w-48 md:w-64 h-auto"
            data-testid="tcpi-logo-standard"
          />
          <img
            src="https://customer-assets.emergentagent.com/job_publish-legacy/artifacts/f3nvjgyv_file_000000000890720cac832b1f0894c497.png"
            alt="The Crown Path Initiative for Women & Young Girls"
            className="w-48 md:w-64 h-auto"
            data-testid="tcpi-logo-women"
          />
        </div>
        <h1 className="font-serif text-4xl md:text-6xl leading-tight">On the path to earning our crowns.</h1>
        <p className="mt-6 text-ivory/80 leading-relaxed max-w-2xl mx-auto">
          A seven-track program for transformation across education, ownership, mentorship, and legacy.
          Built for schools, foundations, and community organizations &mdash; with a dedicated track for women and young girls.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href="https://thecrownpathinitiative.org" target="_blank" rel="noopener noreferrer"
            data-testid="tcpi-official-site"
            className="inline-flex items-center gap-3 bg-gold text-navy px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-white transition-colors">
            Visit thecrownpathinitiative.org
            <span aria-hidden="true">&rarr;</span>
          </a>
          <a href="#tcpi-inquiry" data-testid="tcpi-partner-cta"
            className="border border-ivory/60 text-ivory px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-white hover:text-navy transition-colors">
            Bring TCPI To Your Institution
          </a>
        </div>
      </div>
    </section>
  );
}

function TracksGrid() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="overline">Seven Tracks</div>
          <h2 className="font-serif text-4xl md:text-5xl text-navy mt-2">A complete framework.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRACKS.map((t) => (
            <div key={t.n} className="bg-parchment border border-navy/15 p-8" data-testid={`track-${t.n}`}>
              <div className="font-serif text-5xl text-gold">{t.n}</div>
              <div className="font-serif text-2xl text-navy mt-3">{t.title}</div>
              <p className="mt-3 text-charcoal/80 text-sm leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactStats() {
  return (
    <section className="py-20 md:py-24 bg-navy text-ivory">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
        {IMPACT.map((m) => (
          <div key={m.l}>
            <div className="font-serif text-5xl text-gold"><Counter end={m.v} suffix={m.s} /></div>
            <div className="stat-underline mx-auto" />
            <div className="mt-4 overline text-ivory/80">{m.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialGrid() {
  return (
    <section className="py-20 md:py-28 bg-parchment">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="overline">Testimonials</div>
          <h2 className="font-serif text-4xl text-navy mt-2">From educators, partners, and graduates.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white border border-navy/15 p-8">
              <p className="font-serif text-lg text-navy leading-relaxed italic">&ldquo;{t.body}&rdquo;</p>
              <div className="mt-5 overline">{t.name}</div>
              <div className="text-sm text-charcoal/70 mt-1">{t.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "TCPI Inquiry", project_summary: "" });
  const [loading, setLoading] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/services/inquiry", form);
      toast.success("Thank you. The TCPI team will be in touch.");
      setForm({ ...form, name: "", email: "", phone: "", project_summary: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="tcpi-inquiry" className="py-20 md:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="overline">For Partners</div>
          <h2 className="font-serif text-4xl md:text-5xl text-navy mt-2">Bring TCPI to your institution.</h2>
          <p className="mt-4 text-charcoal/80">Program licensing, speaking, and training requests welcome.</p>
        </div>
        <form onSubmit={send} className="space-y-4 bg-parchment border border-navy/15 p-8" data-testid="tcpi-form">
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
          </div>
          <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold">
            <option>TCPI Inquiry</option>
            <option>Program Licensing</option>
            <option>Speaking Request</option>
            <option>Training Request</option>
          </select>
          <textarea required rows={5} placeholder="Tell us about your institution and timeline" value={form.project_summary} onChange={(e) => setForm({ ...form, project_summary: e.target.value })} className="w-full bg-white border border-navy/15 px-4 py-3 focus:outline-none focus:border-gold" />
          <button disabled={loading} className="w-full bg-navy text-ivory py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60" data-testid="tcpi-submit">
            {loading ? "Sending..." : "Send Inquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}
